import { useEffect, useState } from 'react';
import type { AdminReleaseRun, PromoteClientResponse } from '@boardflight/shared';
import { DESKTOP_PLATFORM_NAME, DesktopPlatform } from '@boardflight/shared';
import type { AdminDesktopArchived, AdminDesktopBuild, AdminDesktopFleetRow, AdminDesktopReleases } from '../../../src/release-types.js';
import { FLEET_DAYS } from '../../../src/release-types.js';
import { ApiError, api, post } from '../api.js';
import { Async, Filter, Modal, Panel, Time, useAsync } from '../ui.js';

/**
 * Desktop app releases (`docs/client-release-plan.md` §4) — which BoardFlight people download and
 * installed apps update to, and the one button that changes it.
 *
 * A build of `main` archives the desktop app and releases nothing, so the owner can try a build on
 * their own computer and Board first. Releasing points `desktop/latest.json` — what the download
 * page lists, and where an app reads its floor — and `desktop/updater.json` — what an installed
 * app updates from — at an archived version, and it is done by CI (`desktop-promote.yml`), which
 * this tab starts. It does not write the bucket and cannot: the console binds none.
 *
 * GitHub answers a dispatch before the run exists, so there is no result to wait on. The tab reads
 * the pointer every 15 seconds until it names the version, and says so; after ten minutes it stops
 * and points at the workflow's runs. A release started from `gh` or the Actions tab shows up here
 * too, from the workflow's own list of runs.
 *
 * The floor is shown and not set here: raising it retires every older copy of the app at once,
 * and that is typed into `gh workflow run desktop-promote.yml -f minimum=…` by somebody reading
 * what it does.
 */

const POLL_MS = 15_000;
const GIVE_UP_MS = 10 * 60_000;

const mb = (bytes: number) => `${(bytes / 1_048_576).toFixed(1)} MB`;

/** `macOS · Windows · Linux (AppImage)`, each a link to the installer at its own address. */
function Builds({ builds }: { builds: AdminDesktopBuild[] }) {
  if (builds.length === 0) return <span className="muted">—</span>;
  return (
    <>
      {builds.map((build, at) => (
        <span key={build.file}>
          {at > 0 ? ' · ' : null}
          <a href={build.url} rel="noreferrer" title={`${build.file} — ${mb(build.size)}, sha256 ${build.sha256}`}>
            {build.label}
          </a>
        </span>
      ))}
    </>
  );
}

export function Releases() {
  const state = useAsync(() => api<AdminDesktopReleases>('/v1/admin/desktop-releases'), []);
  const [asking, setAsking] = useState<AdminDesktopArchived | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A release this tab started and has not yet seen land. */
  const [waiting, setWaiting] = useState<{ version: string; since: number; runUrl: string | null } | null>(null);
  const [now, setNow] = useState(Date.now());

  const data = state.data;
  const landed = waiting !== null && data?.released?.version === waiting.version;
  useEffect(() => {
    if (landed) setWaiting(null);
  }, [landed]);

  const gaveUp = waiting !== null && now - waiting.since > GIVE_UP_MS;
  // Polled while something is on its way — this tab's release, or one GitHub says is running.
  const polling = (waiting !== null && !gaveUp) || data?.pending != null;
  useEffect(() => {
    if (!polling) return;
    const timer = setInterval(() => {
      setNow(Date.now());
      state.reload();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [polling, state.reload]);

  async function release(entry: AdminDesktopArchived, rollback: boolean) {
    setBusy(true);
    setError(null);
    try {
      const answer = await post<PromoteClientResponse>(
        `/v1/admin/desktop-releases/${encodeURIComponent(entry.version)}/promote`,
        { rollback },
      );
      setWaiting({ version: answer.version, since: Date.now(), runUrl: answer.runUrl });
      setNow(Date.now());
      setAsking(null);
      state.reload();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Panel title="Released">
        <Async state={state}>
          {(releases) => (
            <div className="prose">
              {releases.released ? (
                <>
                  <p>
                    <span className="release-version">{releases.released.version}</span> is the released desktop app:
                    the one listed for download.{' '}
                    {releases.released.promotedAt ? (
                      <span className="muted">
                        Released <Time at={Date.parse(releases.released.promotedAt)} />.
                      </span>
                    ) : null}
                  </p>
                  <p className="muted">
                    {releases.released.updates === null
                      ? 'No release has had signed updates yet, so an installed app is updated by downloading it again.'
                      : releases.released.updates === releases.released.version
                        ? `Installed apps update to ${releases.released.updates}.`
                        : `Installed apps are still offered ${releases.released.updates}, not ${releases.released.version}.`}{' '}
                    {releases.released.minimum
                      ? `Apps older than ${releases.released.minimum} are told to update before they open the library.`
                      : 'There is no floor: every version still opens the library.'}
                  </p>
                </>
              ) : (
                <p className="muted">
                  No desktop app is released yet, so nobody is offered one. The first release is made here, like
                  every one after it.
                </p>
              )}
              <Progress
                releases={releases}
                waiting={landed ? null : waiting}
                gaveUp={gaveUp}
              />
              {releases.cannotRelease ? <p className="warn">{releases.cannotRelease}</p> : null}
            </div>
          )}
        </Async>
      </Panel>

      <Panel title="On computers">
        <Async state={state}>
          {(releases) => <Fleet fleet={releases.fleet} released={releases.released?.version ?? null} />}
        </Async>
      </Panel>

      <Panel title="Archive">
        <Async state={state} empty="Nothing is archived.">
          {(releases) => (
            <>
              <p className="note" style={{ padding: '10px 16px 0' }}>
                Every push to main that changes the desktop app archives it here, for every platform at once. Try
                one on your own computer first: each installer is a link, at an address nothing else points to.
              </p>
              {error ? <p className="error" style={{ padding: '0 16px' }}>{error}</p> : null}
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>Installers</th>
                      <th>Updates</th>
                      <th>Archived</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {releases.archived.map((entry) => (
                      <tr key={entry.version}>
                        <td className="mono">{entry.version}</td>
                        <td>
                          <Builds builds={entry.builds} />
                        </td>
                        <td className="muted small">{entry.updates ? 'signed' : 'none'}</td>
                        <td className="muted small">
                          {entry.archivedAt ? <Time at={Date.parse(entry.archivedAt)} /> : '—'}
                        </td>
                        <td>
                          <Action
                            entry={entry}
                            releases={releases}
                            disabled={busy || (waiting !== null && !gaveUp && !landed)}
                            onRelease={() => setAsking(entry)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {releases.archivedTotal > releases.archived.length ? (
                <p className="note" style={{ padding: '0 16px 12px' }}>
                  The newest {releases.archived.length} of {releases.archivedTotal} archived versions.
                </p>
              ) : null}
            </>
          )}
        </Async>
      </Panel>

      {asking && data ? (
        <Confirm
          entry={asking}
          releases={data}
          busy={busy}
          error={error}
          onCancel={() => {
            setAsking(null);
            setError(null);
          }}
          onConfirm={(rollback) => void release(asking, rollback)}
        />
      ) : null}
    </>
  );
}

/** What is on its way, from this tab's own press or from GitHub's list of runs — and what failed. */
function Progress({
  releases,
  waiting,
  gaveUp,
}: {
  releases: AdminDesktopReleases;
  waiting: { version: string; since: number; runUrl: string | null } | null;
  gaveUp: boolean;
}) {
  const runLink = (url: string | null, label = 'the run') =>
    url ? (
      <a href={url} target="_blank" rel="noreferrer">
        {label}
      </a>
    ) : null;

  if (releases.pending) {
    return (
      <p>
        <span className="pill">releasing</span> {releases.pending.version ?? 'a version'} —{' '}
        {releases.pending.status === 'queued' ? 'queued behind any release in progress' : 'running'}
        {releases.pending.actor ? `, started by ${releases.pending.actor}` : ''}. {runLink(releases.pending.url, 'Watch the run')}
      </p>
    );
  }
  if (waiting && gaveUp) {
    return (
      <p className="warn">
        {waiting.version} has not landed after ten minutes.{' '}
        {runLink(waiting.runUrl ?? releases.workflowUrl, 'See the release runs')}
      </p>
    );
  }
  if (waiting) {
    const failed = failedSince(releases.last, waiting.version, waiting.since);
    if (failed) {
      return (
        <p className="error">
          The release of {waiting.version} failed. {runLink(failed.url, 'See the run')} for why — the
          workflow says what it refused.
        </p>
      );
    }
    return (
      <p>
        <span className="pill">releasing</span> {waiting.version} — GitHub has it; this page checks every 15
        seconds. {runLink(waiting.runUrl)}
      </p>
    );
  }
  if (releases.last && releases.last.conclusion !== 'success') {
    return (
      <p className="muted">
        The last release{releases.last.version ? ` (${releases.last.version})` : ''} ended{' '}
        {releases.last.conclusion ?? releases.last.status}. {runLink(releases.last.url, 'See the run')}
      </p>
    );
  }
  return null;
}

/** The finished run for this version that started after the press, if it did not succeed. */
function failedSince(last: AdminReleaseRun | null, version: string, since: number): AdminReleaseRun | null {
  if (!last || last.version !== version || last.conclusion === 'success') return null;
  // A minute's slack for the two clocks: GitHub's run timestamp and this browser's.
  return Date.parse(last.createdAt) >= since - 60_000 ? last : null;
}

/** The row's one control, or the reason it has none. */
function Action({
  entry,
  releases,
  disabled,
  onRelease,
}: {
  entry: AdminDesktopArchived;
  releases: AdminDesktopReleases;
  disabled: boolean;
  onRelease: () => void;
}) {
  if (releases.released?.version === entry.version) return <span className="pill on">released</span>;
  if (entry.problem !== null) {
    return (
      <span className="muted small" title={entry.problem}>
        cannot be released
      </span>
    );
  }
  return (
    <button
      className={entry.older ? undefined : 'primary'}
      disabled={disabled || releases.cannotRelease !== null || releases.pending !== null}
      title={releases.cannotRelease ?? undefined}
      onClick={onRelease}
    >
      {entry.older ? 'Roll back to this' : 'Release'}
    </button>
  );
}

/**
 * Asked every time, not only for a rollback: a release reaches every installed app at its next
 * update check, and a misclick on a table row is not how that should happen.
 */
function Confirm({
  entry,
  releases,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  entry: AdminDesktopArchived;
  releases: AdminDesktopReleases;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (rollback: boolean) => void;
}) {
  const released = releases.released;
  const rollback = entry.older;
  return (
    <Modal title={rollback ? `Roll back to ${entry.version}?` : `Release ${entry.version}?`} onClose={onCancel}>
      <div className="prose">
        {rollback && released ? (
          <>
            <p>
              {entry.version} is listed for download instead of {released.version}.
            </p>
            <p className="warn">
              Nothing changes on a computer that already has {released.version}: an installed app only ever
              updates upwards. A bad release is fixed by releasing a newer one.
            </p>
          </>
        ) : (
          <p>
            {entry.version} is listed for download from now on{released ? `, instead of ${released.version}` : ''}
            {entry.updates
              ? ', and every installed app is offered it at its next update check.'
              : '. It was built without signed updates, so no installed app is offered it.'}{' '}
            The release hashes every installer against the archive, then runs the production smoke test.
          </p>
        )}
        {released?.minimum ? (
          <p className="muted">The floor stays at {released.minimum}.</p>
        ) : null}
        {error ? <p className="error">{error}</p> : null}
        <div className="row" style={{ marginTop: 12 }}>
          <button className={rollback ? 'danger' : 'primary'} disabled={busy} onClick={() => onConfirm(rollback)}>
            {busy ? 'Starting…' : rollback ? `Roll back to ${entry.version}` : `Release ${entry.version}`}
          </button>
          <button onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** What a failing computer says, in a few words, by `UpdateFailureReason`. */
const FAILURE_WORDS: Record<keyof AdminDesktopFleetRow['failing'], string> = {
  read_only: 'runs where it cannot replace itself',
  permission: 'no administrator password',
  download: 'could not download',
  signature: 'did not pass its check',
  other: 'other',
};

/**
 * Which versions the app is on where it runs, and which computers say their own update is failing
 * (migration 0086). An app older than 0.1.22 cannot say, so a version that stays put with nothing
 * failing beside it may still be one that fails: it says nothing either way.
 */
/** A desktop row's platform by name, or a dash for one this console does not know. */
function platformName(value: string | null): string {
  const platform = DesktopPlatform.safeParse(value);
  return platform.success ? DESKTOP_PLATFORM_NAME[platform.data] : '—';
}

/** One version's computers, over whichever systems the filter lets through. */
interface FleetVersion {
  version: string | null;
  computers: number;
  failing: Partial<Record<keyof AdminDesktopFleetRow['failing'], number>>;
  /** Computers per system, for the line under an "every system" row. */
  systems: Map<string, number>;
}

function Fleet({ fleet, released }: { fleet: AdminDesktopFleetRow[]; released: string | null }) {
  // A filter on the fleet, not a split of it (owner, 2026-10-28): how many, on what, failing.
  const [system, setSystem] = useState('');
  if (fleet.length === 0) {
    return <p className="muted" style={{ padding: '10px 16px' }}>No computer running the app was heard from in the last {FLEET_DAYS} days.</p>;
  }
  const systems = [...new Set(fleet.map((row) => row.platform ?? ''))];
  const rows = fleet.filter((row) => system === '' || (row.platform ?? '') === system);
  const byVersion = new Map<string, FleetVersion>();
  for (const row of rows) {
    const key = row.version ?? '';
    const group: FleetVersion = byVersion.get(key) ?? { version: row.version, computers: 0, failing: {}, systems: new Map() };
    group.computers += row.computers;
    for (const [reason, count] of Object.entries(row.failing) as [keyof AdminDesktopFleetRow['failing'], number][]) {
      group.failing[reason] = (group.failing[reason] ?? 0) + count;
    }
    const name = platformName(row.platform);
    group.systems.set(name, (group.systems.get(name) ?? 0) + row.computers);
    byVersion.set(key, group);
  }
  const versions = [...byVersion.values()].sort((a, b) => b.computers - a.computers);
  const total = versions.reduce((sum, one) => sum + one.computers, 0);
  const onReleased = versions.find((one) => released !== null && one.version === released)?.computers ?? 0;
  const failingTotal = versions.reduce(
    (sum, one) => sum + Object.values(one.failing).reduce((a, b) => a + (b ?? 0), 0),
    0,
  );

  return (
    <>
      <div className="stats">
        <div className="stat"><span className="stat-label">Computers</span><span className="stat-value">{total}</span></div>
        <div className="stat">
          <span className="stat-label">On {released ?? 'the released version'}</span>
          <span className="stat-value">{total ? `${Math.round((onReleased / total) * 100)}%` : '—'}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Failing to update</span>
          <span className={failingTotal ? 'stat-value error' : 'stat-value'}>{failingTotal}</span>
        </div>
        <div className="stat stat-filter">
          <Filter
            label="System"
            value={system}
            onChange={setSystem}
            options={[{ value: '', label: 'Every system' }, ...systems.map((value) => ({ value, label: platformName(value || null) }))]}
          />
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Version</th>
              <th className="num">Computers</th>
              <th className="num">Share</th>
              <th>Failing to update</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((one) => {
              const failing = Object.entries(one.failing) as [keyof AdminDesktopFleetRow['failing'], number][];
              return (
                <tr key={one.version ?? ''}>
                  <td className="mono">
                    {one.version ?? '—'}
                    {released !== null && one.version === released ? <span className="pill on" style={{ marginLeft: 6 }}>released</span> : null}
                    {system === '' && one.systems.size > 1 ? (
                      <div className="muted small">{[...one.systems].map(([name, n]) => `${name} ${n}`).join(' · ')}</div>
                    ) : null}
                  </td>
                  <td className="num">{one.computers}</td>
                  <td className="num muted">{total ? `${Math.round((one.computers / total) * 100)}%` : '—'}</td>
                  <td className="small">
                    {failing.length === 0 ? (
                      <span className="muted">none</span>
                    ) : (
                      failing.map(([reason, count]) => (
                        <span key={reason} className="warn" style={{ display: 'block' }}>
                          {count} · {FAILURE_WORDS[reason]}
                        </span>
                      ))
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="note" style={{ padding: '0 16px 10px' }}>
        Heard from in the last {FLEET_DAYS} days, excluded accounts left out. Apps older than 0.1.22 cannot report a
        failing update.
      </p>
    </>
  );
}

